import React from 'react';

import { useIntl } from 'react-intl';
import { Button, Input, Modal, Radio, Select, Table, Tag, Tooltip, Typography } from 'antd';
import type { RadioChangeEvent } from 'antd';
import classNames from 'classnames';
import {
  AimOutlined,
  QrcodeOutlined,
  CheckOutlined,
  DownOutlined,
  QuestionCircleFilled,
  SearchOutlined,
} from '@ant-design/icons';

import IntlMessages from '@src/util/IntlMessages';
import { ALLOWED_ROLES } from '@src/packages/utils';
import { KeyValueWidget } from '@src/components/KeyValueWidget';
import MobileSelectSheet from '@src/components/MobileSelectSheet';
import CircleButton from '@src/components/Buttons/CircleButton';
import Title from '@src/components/BoxContainer/components/Title';
import AvatarLocation from '@src/components/Location/AvatarLocation';
import DivisionIconLabel from '@src/components/Division/DivisionIconLabel';
import LocationGroup from '@src/packages/locations/screens/LocationGroup';
import ComponentBlocker from '@src/components/ComponentBlocker';

import { LocationTypes } from '@src/models/interfaces/constants';
import type {
  LocationCombinerIsAGroup,
  TriggerLocation,
} from '@src/models/interfaces/trigger.interface';

import { getLocationCombinerStatus } from './locationCombinerStatus';
import { useLocationCombiner } from './useLocationCombiner';
import type { LocationCombinerProps } from './LocationCombiner.types';
import styles from './styles.module.less';

export { getLocationCombinerStatus };
export type { LocationCombinerProps };

/**
 * Combines location and location group selection in a single component.
 *
 * The controlled `value` (and the `onChange` payload) is a `LocationCombinerValue`:
 *   - isAGroup: -1 → None selected, data: null
 *   - isAGroup: false → Locations selected, data: TriggerLocation[] (objects with at least id)
 *   - isAGroup: true → Location group selected, data: TriggerLocationGroup (object with at least id)
 *
 * All state, data fetching and handlers live in {@link useLocationCombiner}; this
 * component is presentational only.
 */
function LocationCombiner({
  divisionId = null,
  divisionIds = null,
  value = null,
  onChange = () => {},
  className,
  showAddButton = true,
  emptyOptionLabel = 'triggers.view.sectionTitle.none',
  selectionDisplay = 'select',
  locationModeLabel = 'general.locations',
  groupModeLabel = 'general.locationsGroup',
  headingLabel,
  isMobile = false,
  disabled = false,
}: LocationCombinerProps) {
  const intl = useIntl();

  const {
    mode,
    primaryDivisionId,
    locationGroupsMerged,
    allLocations,
    selectedLocationIds,
    selectedGroupId,
    isHelpVisible,
    isGroupFormOpen,
    isTypeFilterOpen,
    isGroupPickerOpen,
    groupSearch,
    setGroupSearch,
    openGroupPicker,
    closeGroupPicker,
    markerSearch,
    markerTypeFilter,
    markerTablePageSize,
    radioDisabled,
    groupSelectDisabled,
    locationsSelectDisabled,
    mainLoading,
    onChangeMode,
    onChangeLocationGroups,
    onChangeLocations,
    setMarkerSearch,
    setMarkerTypeFilter,
    setMarkerTableElement,
    isLocationOptionDisabled,
    openHelp,
    closeHelp,
    openTypeFilter,
    closeTypeFilter,
    openGroupForm,
    closeGroupForm,
  } = useLocationCombiner({ divisionId, divisionIds, value, onChange, disabled });

  const markerTypes = Object.values(LocationTypes);
  const normalizedSearch = markerSearch.toLocaleLowerCase(intl.locale);
  const filteredLocationGroups = locationGroupsMerged.filter(group =>
    group.name
      .toLocaleLowerCase(intl.locale)
      .includes(groupSearch.trim().toLocaleLowerCase(intl.locale)),
  );
  const filteredLocations = allLocations
    .filter(location => {
      const matchesSearch = location.name.toLocaleLowerCase(intl.locale).includes(normalizedSearch);
      const matchesType = markerTypeFilter === 'all' || location.type === markerTypeFilter;

      return matchesSearch && matchesType;
    })
    .sort(
      (first, second) =>
        Number(selectedLocationIds.includes(second.id)) -
        Number(selectedLocationIds.includes(first.id)),
    );
  const visibleLocationIds = new Set(filteredLocations.map(location => location.id));

  const locationRowSelection = {
    selectedRowKeys: selectedLocationIds,
    preserveSelectedRowKeys: true,
    onChange: (selectedRowKeys: React.Key[]) => {
      const selectedOutsideFilters = selectedLocationIds.filter(id => !visibleLocationIds.has(id));
      onChangeLocations([...selectedOutsideFilters, ...selectedRowKeys.map(key => String(key))]);
    },
    getCheckboxProps: (location: TriggerLocation) => ({
      disabled: locationsSelectDisabled || isLocationOptionDisabled(location),
    }),
    renderCell: (
      checked: boolean,
      location: TriggerLocation,
      _index: number,
      originNode: React.ReactNode,
    ) => {
      if (checked || locationsSelectDisabled || !isLocationOptionDisabled(location)) {
        return originNode;
      }

      return (
        <Tooltip
          title={intl.formatMessage(
            { id: 'triggers.create.markers.sameTypeDisabled.tooltip' },
            { type: intl.formatMessage({ id: `locations.${location.type.toLowerCase()}` }) },
          )}
        >
          <span className={styles.disabledLocationCheckbox}>{originNode}</span>
        </Tooltip>
      );
    },
  };

  return (
    <div
      className={classNames('gx-w-100', styles.locationCombinerMain, className, {
        [styles.locationTableLayout]: selectionDisplay === 'table',
      })}
    >
      {headingLabel && (
        <div className={styles.locationModeHeading}>
          <Typography.Title level={4}>
            <IntlMessages id={headingLabel} />
          </Typography.Title>
          <Button
            aria-label={intl.formatMessage({ id: 'general.help' })}
            className="gx-m-0"
            type="text"
            onClick={openHelp}
          >
            <QuestionCircleFilled />
          </Button>
        </div>
      )}
      <div
        className={classNames('gx-flex-row gx-align-items-center gx-w-100', {
          [styles.locationModeRow]: selectionDisplay === 'table',
        })}
      >
        <Radio.Group
          className={classNames({ [styles.locationModeGroup]: selectionDisplay === 'table' })}
          disabled={radioDisabled}
          value={mode}
          defaultValue={-1}
          onChange={(val: RadioChangeEvent) =>
            onChangeMode(val.target.value as LocationCombinerIsAGroup)
          }
        >
          <Radio.Button value={-1}>
            <IntlMessages id={emptyOptionLabel} />
          </Radio.Button>
          <Radio.Button value={false}>
            <IntlMessages id={locationModeLabel} />
          </Radio.Button>
          <Radio.Button value>
            <IntlMessages id={groupModeLabel} />
          </Radio.Button>
        </Radio.Group>
        {!headingLabel && (
          <Button
            aria-label={intl.formatMessage({ id: 'general.help' })}
            className="gx-m-0"
            type="text"
            onClick={openHelp}
          >
            <QuestionCircleFilled />
          </Button>
        )}
      </div>
      {mode === -1 && selectionDisplay === 'table' && (
        <Typography.Text className={styles.locationModeDescription} type="secondary">
          <IntlMessages id="triggers.create.markers.mode.any.description" />
        </Typography.Text>
      )}
      {mode !== -1 && (
        <div
          className={classNames('gx-w-100', {
            [styles.locationTableContent]: selectionDisplay === 'table' && mode === false,
          })}
        >
          {mode ? (
            <KeyValueWidget
              value={
                <div className="gx-flex-row gx-guarnic-gap-2">
                  <div className="gx-flex-1">
                    {isMobile ? (
                      <>
                        <button
                          aria-label={intl.formatMessage({ id: 'general.select.locationsGroup' })}
                          aria-haspopup="dialog"
                          aria-expanded={isGroupPickerOpen}
                          className={styles.mobileMarkerTypeTrigger}
                          type="button"
                          disabled={groupSelectDisabled}
                          onClick={openGroupPicker}
                        >
                          <span>
                            {locationGroupsMerged.find(group => group.id === selectedGroupId)
                              ?.name ?? intl.formatMessage({ id: 'general.select.locationsGroup' })}
                          </span>
                          <DownOutlined aria-hidden="true" />
                        </button>
                        <MobileSelectSheet
                          contentSized
                          title={intl.formatMessage({ id: groupModeLabel })}
                          visible={isGroupPickerOpen}
                          searchValue={groupSearch}
                          searchAriaLabel={intl.formatMessage({ id: 'general.search' })}
                          searchPlaceholder={intl.formatMessage({
                            id: 'general.search.placeholder',
                          })}
                          onSearch={setGroupSearch}
                          onClose={closeGroupPicker}
                        >
                          <div
                            className={styles.mobileMarkerTypeOptions}
                            role="listbox"
                            aria-label={intl.formatMessage({ id: groupModeLabel })}
                          >
                            {filteredLocationGroups.map(group => (
                              <button
                                key={group.id}
                                role="option"
                                aria-selected={selectedGroupId === group.id}
                                className={styles.mobileMarkerTypeOption}
                                type="button"
                                onClick={() => {
                                  onChangeLocationGroups(group.id);
                                  closeGroupPicker();
                                }}
                              >
                                <span>{group.name}</span>
                                {selectedGroupId === group.id ? (
                                  <CheckOutlined aria-hidden="true" />
                                ) : null}
                              </button>
                            ))}
                            {filteredLocationGroups.length === 0 ? (
                              <Typography.Text
                                role="status"
                                className={styles.locationModeDescription}
                                type="secondary"
                              >
                                <IntlMessages id="general.empty.state" />
                              </Typography.Text>
                            ) : null}
                          </div>
                        </MobileSelectSheet>
                      </>
                    ) : (
                      <Select
                        data-testid="location-group-select"
                        showSearch
                        optionFilterProp="title"
                        className="gx-w-100 gx-m-0"
                        placeholder={<IntlMessages id="general.select.locationsGroup" />}
                        value={selectedGroupId}
                        onChange={onChangeLocationGroups}
                        disabled={groupSelectDisabled}
                      >
                        {locationGroupsMerged.map(item => (
                          <Select.Option key={item.id} title={item.name} value={item.id}>
                            <div className="gx-flex-row gx-align-content-center gx-guarnic-gap-2">
                              <div className="gx-align-content-center">
                                <CircleButton iconName="workspaces" iconSize="sm" />
                              </div>
                              <div className="gx-flex-row gx-guarnic-gap-2">
                                <div className="gx-align-content-center">{item.name}</div>
                                <div className="gx-align-content-center">
                                  {item.divId != null && (
                                    <DivisionIconLabel divId={String(item.divId)} />
                                  )}
                                </div>
                              </div>
                            </div>
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                  </div>
                  {showAddButton && (
                    <ComponentBlocker
                      allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.LOCATION_GROUPS.CREATE}
                      divId={primaryDivisionId}
                    >
                      <div className="gx-flex-2">
                        <CircleButton onClick={openGroupForm} iconName="add" />
                      </div>
                    </ComponentBlocker>
                  )}
                </div>
              }
            />
          ) : selectionDisplay === 'table' ? (
            <div className={styles.locationsTableContainer}>
              <div
                role="group"
                aria-label={intl.formatMessage({ id: 'triggers.create.markers.counts' })}
                className={styles.markerTypeCounts}
              >
                {markerTypes.map(type => (
                  <Tag key={type}>
                    {type === LocationTypes.GPS ? (
                      <AimOutlined />
                    ) : type === LocationTypes.QR ? (
                      <QrcodeOutlined />
                    ) : (
                      <span>{type === LocationTypes.NFC ? 'NFC' : 'BLE'}</span>
                    )}
                    <IntlMessages id={`locations.${type.toLowerCase()}`} />{' '}
                    <IntlMessages
                      id="triggers.create.markers.count"
                      values={{
                        count: allLocations.filter(
                          location =>
                            location.type === type && selectedLocationIds.includes(location.id),
                        ).length,
                      }}
                    />
                  </Tag>
                ))}
              </div>
              <div className={styles.markerTableFilters}>
                <Input
                  aria-label={intl.formatMessage({
                    id: 'triggers.create.markers.search.placeholder',
                  })}
                  allowClear
                  className={styles.markerSearch}
                  placeholder={intl.formatMessage({
                    id: 'triggers.create.markers.search.placeholder',
                  })}
                  prefix={<SearchOutlined />}
                  value={markerSearch}
                  onChange={event => setMarkerSearch(event.target.value)}
                />
                {isMobile ? (
                  <>
                    <button
                      aria-expanded={isTypeFilterOpen}
                      aria-haspopup="dialog"
                      aria-label={intl.formatMessage({
                        id: 'triggers.create.markers.typeFilter.label',
                      })}
                      className={styles.mobileMarkerTypeTrigger}
                      type="button"
                      onClick={openTypeFilter}
                    >
                      <span>
                        {markerTypeFilter === 'all' ? (
                          <IntlMessages id="triggers.create.markers.typeFilter.all" />
                        ) : (
                          <IntlMessages id={`locations.${markerTypeFilter.toLowerCase()}`} />
                        )}
                      </span>
                      <DownOutlined aria-hidden="true" />
                    </button>
                    <MobileSelectSheet
                      height={360}
                      title={intl.formatMessage({
                        id: 'triggers.create.markers.typeFilter.label',
                      })}
                      visible={isTypeFilterOpen}
                      onClose={closeTypeFilter}
                    >
                      <div className={styles.mobileMarkerTypeOptions}>
                        <button
                          aria-pressed={markerTypeFilter === 'all'}
                          className={styles.mobileMarkerTypeOption}
                          type="button"
                          onClick={() => {
                            setMarkerTypeFilter('all');
                            closeTypeFilter();
                          }}
                        >
                          <IntlMessages id="triggers.create.markers.typeFilter.all" />
                          {markerTypeFilter === 'all' ? <CheckOutlined aria-hidden="true" /> : null}
                        </button>
                        {markerTypes.map(type => (
                          <button
                            aria-pressed={markerTypeFilter === type}
                            className={styles.mobileMarkerTypeOption}
                            key={type}
                            type="button"
                            onClick={() => {
                              setMarkerTypeFilter(type);
                              closeTypeFilter();
                            }}
                          >
                            <IntlMessages id={`locations.${type.toLowerCase()}`} />
                            {markerTypeFilter === type ? (
                              <CheckOutlined aria-hidden="true" />
                            ) : null}
                          </button>
                        ))}
                      </div>
                    </MobileSelectSheet>
                  </>
                ) : (
                  <Select
                    aria-label={intl.formatMessage({
                      id: 'triggers.create.markers.typeFilter.label',
                    })}
                    className={styles.markerTypeFilter}
                    value={markerTypeFilter}
                    onChange={type => setMarkerTypeFilter(type)}
                  >
                    <Select.Option value="all">
                      <IntlMessages id="triggers.create.markers.typeFilter.all" />
                    </Select.Option>
                    {markerTypes.map(type => (
                      <Select.Option key={type} value={type}>
                        <IntlMessages id={`locations.${type.toLowerCase()}`} />
                      </Select.Option>
                    ))}
                  </Select>
                )}
              </div>
              <div className={styles.markerTable} ref={setMarkerTableElement}>
                <Table<TriggerLocation>
                  columns={[
                    {
                      dataIndex: 'name',
                      key: 'name',
                      title: <IntlMessages id="triggers.create.markers.table.marker" />,
                      width: '55%',
                      render: (_name, location) => (
                        <div className={styles.locationNameCell}>
                          <AvatarLocation location={location} type="primary" />
                          <span>{location.name}</span>
                        </div>
                      ),
                    },
                    {
                      dataIndex: 'type',
                      key: 'type',
                      title: <IntlMessages id="triggers.create.markers.table.type" />,
                      width: '15%',
                      render: type => (
                        <IntlMessages id={`locations.${String(type).toLowerCase()}`} />
                      ),
                    },
                    {
                      key: 'division',
                      title: <IntlMessages id="general.division" />,
                      width: '22%',
                      render: (_value, location) =>
                        location.divId ? (
                          <DivisionIconLabel divId={location.divId} plainText />
                        ) : (
                          <IntlMessages id="location.group.by.organization" />
                        ),
                    },
                  ]}
                  dataSource={filteredLocations}
                  loading={mainLoading}
                  locale={{
                    emptyText: (
                      <IntlMessages
                        id={
                          allLocations.length
                            ? 'triggers.create.markers.filteredEmpty'
                            : 'triggers.create.markers.empty'
                        }
                      />
                    ),
                  }}
                  pagination={
                    isMobile ? false : { pageSize: markerTablePageSize, showSizeChanger: false }
                  }
                  rowKey="id"
                  scroll={isMobile ? { x: 620 } : undefined}
                  rowSelection={{
                    ...locationRowSelection,
                    columnWidth: '8%',
                    hideSelectAll: true,
                  }}
                  size="middle"
                  tableLayout="fixed"
                  onRow={location => ({
                    className:
                      locationsSelectDisabled || isLocationOptionDisabled(location)
                        ? styles.disabledLocationRow
                        : undefined,
                    onClick: event => {
                      if (
                        (event.target as HTMLElement).closest(
                          'button, a, input, label, .ant-checkbox-wrapper',
                        ) ||
                        locationsSelectDisabled ||
                        isLocationOptionDisabled(location)
                      ) {
                        return;
                      }

                      onChangeLocations(
                        selectedLocationIds.includes(location.id)
                          ? selectedLocationIds.filter(id => id !== location.id)
                          : [...selectedLocationIds, location.id],
                      );
                    },
                  })}
                />
              </div>
            </div>
          ) : (
            <KeyValueWidget
              value={
                <Select
                  data-testid="locations-select"
                  mode="multiple"
                  showSearch
                  allowClear
                  optionFilterProp="title"
                  className={styles.locationsSelect}
                  placeholder={<IntlMessages id="general.select.locations" />}
                  value={selectedLocationIds}
                  onChange={onChangeLocations}
                  disabled={locationsSelectDisabled}
                >
                  {allLocations.map(loc => (
                    <Select.Option
                      key={loc.id}
                      value={loc.id}
                      title={loc.name}
                      disabled={isLocationOptionDisabled(loc)}
                      style={{ height: '45px' }}
                    >
                      <div className="gx-flex-row gx-align-items-center gx-guarnic-gap-1">
                        <AvatarLocation location={loc} type="primary" />
                        <span>{loc.name}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              }
            />
          )}
        </div>
      )}

      <Modal
        title={intl.formatMessage({ id: 'general.help' })}
        open={isHelpVisible}
        footer={null}
        onCancel={closeHelp}
      >
        <div>
          <Title.SubTitle value={<IntlMessages id="general.locations" />} />
          <span>
            <IntlMessages id="location.help.locations.description" />
          </span>
        </div>

        <div className="gx-mt-2">
          <Title.SubTitle value={<IntlMessages id="general.locationsGroup" />} />
          <span>
            <IntlMessages id="location.help.locationsGroup.description" />
          </span>
        </div>
      </Modal>

      {isGroupFormOpen && <LocationGroup onClose={closeGroupForm} divisionId={primaryDivisionId} />}
    </div>
  );
}

export default React.memo(LocationCombiner);
